import WebHttpClient from '~src/httpClient/WebHttpClient';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { GetLocalAuthConfigurationRequest, ILocalAuthPolicyResult } from '../../ts/ServerApi/requests/GetLocalAuthConfigurationRequest';
import { IPasswordPolicy } from './validation/IPasswordPolicy';
import { MaximumPasswordLengthPolicy } from './validation/MaximumPasswordLengthPolicy';
import { MinimumPasswordLengthPolicy } from './validation/MinimumPasswordLengthPolicy';
import { MustContainLettersPolicy } from './validation/MustContainLettersPolicy';
import { MustContainNumbersPolicy } from './validation/MustContainNumbersPolicy';
import { MustContainSymbolsPolicy } from './validation/MustContainSymbolsPolicy';
import { MustContainUppercaseLettersPolicy } from './validation/MustContainUppercaseLettersPolicy';

export class LocalAuthConfiguration {
	private activePasswordPolicies: IPasswordPolicy[] = [];

	/**
	 * Checks the provided candidate password against the configured policies
	 * to see if it should be allowed or not.
	 * 
	 * @returns a list of policies that rejected this candidate.
	 */
	public performPasswordValidation(passwordCandidate: string): IPasswordPolicy[] {
		if (!FeatureFlags.passwordPoliciesEnabled) {
			return [];
		}

		return this.activePasswordPolicies.filter(x => !x.isValid(passwordCandidate));
	}

	/**
	 * Will fetch the current configuration from the server and return it in the promise
	 * content once finished.
	 */
	public static fetchCurrentLocalAuthConfiguration(): Promise<LocalAuthConfiguration> {
		if (!FeatureFlags.passwordPoliciesEnabled) {
			// Feature is off, return the stub with no policies.
			return Promise.resolve(LocalAuthConfiguration.default());
		}

		return WebHttpClient.sendRequest(new GetLocalAuthConfigurationRequest()).then((result) => {
			return LocalAuthConfiguration.createFromServerConfig(result);
		});
	}

	private static createFromServerConfig(response: ILocalAuthPolicyResult): LocalAuthConfiguration {
		const config = new LocalAuthConfiguration();
		if (response.isMustContainLettersPolicyEnabled) {
			config.activePasswordPolicies.push(MustContainLettersPolicy.getPolicy());
		}
		if (response.isMustContainNumbersPolicyEnabled) {
			config.activePasswordPolicies.push(MustContainNumbersPolicy.getPolicy());
		}
		if (response.isMustContainSymbolsPolicyEnabled) {
			config.activePasswordPolicies.push(MustContainSymbolsPolicy.getPolicy());
		}
		if (response.isMustContainUppercasePolicyEnabled) {
			config.activePasswordPolicies.push(MustContainUppercaseLettersPolicy.getPolicy());
		}
		if (response.isMinimumPasswordLengthPolicyEnabled && response.minimumPasswordLength > 0) {
			config.activePasswordPolicies.push(new MinimumPasswordLengthPolicy(response.minimumPasswordLength));
		}
		if (response.isMaximumPasswordLengthPolicyEnabled && response.maximumPasswordLength > 0) {
			config.activePasswordPolicies.push(new MaximumPasswordLengthPolicy(response.maximumPasswordLength));
		}
		return config;
	}

	public static default(): LocalAuthConfiguration {
		return new LocalAuthConfiguration();
	}
}
